import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Play, 
  Square, 
  RefreshCw, 
  Download, 
  Server, 
  Activity, 
  MemoryStick, 
  Cpu, 
  Network,
  AlertCircle,
  CheckCircle,
  XCircle,
  Pause
} from 'lucide-react';
import { apiCallJson, apiCall } from './Api';
import { getCurrentToken } from './SessionManager';

const DockerLogsViewer = ({ user }) => {
  const [containers, setContainers] = useState([]);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [logs, setLogs] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [logFilter, setLogFilter] = useState('');
  const [maxLines, setMaxLines] = useState(500);
  
  const eventSourceRef = useRef(null);
  const logsEndRef = useRef(null);

  const loadContainers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCallJson('/api/docker/containers');
      setContainers(data.containers);
    } catch (error) {
      console.error('Failed to load containers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadContainerStats = useCallback(async (containerId) => {
    if (!containerId) return;
    
    try {
      // Try the regular stats endpoint first
      const statsData = await apiCallJson(`/api/docker/containers/${containerId}/stats`);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load stats:', error);
      
      // Try CLI fallback if available
      try {
        const statsData = await apiCallJson(`/api/docker/containers/${containerId}/stats-cli`);
        setStats(statsData);
      } catch (fallbackError) {
        console.error('Stats CLI fallback also failed:', fallbackError);
        setStats({
          cpu_percent: 0,
          memory_percent: 0,
          network_rx: 0,
          network_tx: 0,
          error: 'Stats unavailable'
        });
      }
    }
  }, []);

  const loadStaticLogs = useCallback(async (containerId, tail = 100) => {
    if (!containerId) return;
    
    try {
      const logsData = await apiCallJson(`/api/docker/containers/${containerId}/logs?tail=${tail}`);
      setLogs(logsData.logs.map(log => ({
        message: log,
        timestamp: Date.now(),
        type: 'log'
      })));
    } catch (error) {
      console.error('Failed to load logs:', error);
    }
  }, []);

  const startLogStream = useCallback((containerId) => {
    if (!containerId || isStreaming) return;

    const eventSource = new EventSource(
      `/api/docker/containers/${containerId}/logs/stream`,
      {
        headers: {
          'Authorization': `Bearer ${getCurrentToken()}`
        }
      }
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'log') {
          setLogs(prevLogs => {
            const newLogs = [...prevLogs, {
              message: data.message,
              timestamp: data.timestamp,
              type: 'log'
            }];
            
            // Keep only the last maxLines
            if (newLogs.length > maxLines) {
              return newLogs.slice(-maxLines);
            }
            return newLogs;
          });
        } else if (data.type === 'error') {
          setLogs(prevLogs => [...prevLogs, {
            message: `ERROR: ${data.message}`,
            timestamp: data.timestamp,
            type: 'error'
          }]);
        }
      } catch (error) {
        console.error('Failed to parse log data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('EventSource failed:', error);
      setIsStreaming(false);
    };

    eventSourceRef.current = eventSource;
    setIsStreaming(true);
  }, [isStreaming, maxLines]);

  const stopLogStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const restartContainer = useCallback(async (containerId) => {
    if (!containerId) return;
    
    if (window.confirm('Are you sure you want to restart this container?')) {
      try {
        await apiCall(`/api/docker/containers/${containerId}/restart`, {
          method: 'POST'
        });
        await loadContainers();
        alert('Container restarted successfully');
      } catch (error) {
        console.error('Failed to restart container:', error);
        alert('Failed to restart container');
      }
    }
  }, [loadContainers]);

  const exportLogs = useCallback(() => {
    if (logs.length === 0) return;
    
    const logText = logs.map(log => 
      `[${new Date(log.timestamp).toISOString()}] ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedContainer?.name || 'container'}_logs_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [logs, selectedContainer]);

  const scrollToBottom = useCallback(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'exited':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'paused':
        return <Pause className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'text-green-600 bg-green-100';
      case 'exited':
        return 'text-red-600 bg-red-100';
      case 'paused':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredLogs = logs.filter(log => 
    logFilter === '' || log.message.toLowerCase().includes(logFilter.toLowerCase())
  );

  useEffect(() => {
    loadContainers();
  }, [loadContainers]);

  useEffect(() => {
    if (selectedContainer && autoRefresh) {
      const interval = setInterval(() => {
        loadContainerStats(selectedContainer.id);
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [selectedContainer, autoRefresh, loadContainerStats]);

  useEffect(() => {
    scrollToBottom();
  }, [logs, scrollToBottom]);

  useEffect(() => {
    return () => {
      stopLogStream();
    };
  }, [stopLogStream]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Docker Logs Viewer</h1>
          <p className="mt-1 text-sm text-gray-500"></p>
        </div>
        <div className="mb-8 flex items-center space-x-4">
          <button
            onClick={loadContainers}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      <hr className="border-t border-gray-300" />

      {/* Main Content */}
      <div className=" py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Container List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Containers ({containers.length})
                </h3>
                <div className="space-y-3 overflow-y-auto">
                  {containers.map(container => (
                    <div
                      key={container.id}
                      onClick={() => {
                        setSelectedContainer(container);
                        setLogs([]);
                        stopLogStream();
                        loadStaticLogs(container.id);
                        loadContainerStats(container.id);
                      }}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedContainer?.id === container.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${container.is_self ? 'ring-2 ring-green-200' : ''}`}  // Highlight own containers
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Server className="w-4 h-4 text-gray-600" />
                          <span className="font-medium text-sm">{container.name}</span>
                          {container.is_self && (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              App
                            </span>
                          )}
                        </div>
                        {getStatusIcon(container.status)}
                      </div>
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(container.status)}`}>
                          {container.status}
                        </span>
                        <div className="text-xs text-gray-500 mt-1 truncate">
                          {container.image}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Logs and Stats */}
          <div className="lg:col-span-3">
            {selectedContainer ? (
              <div className="space-y-6">
                {/* Container Stats */}
                {stats && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {selectedContainer.name} - Statistics
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="flex items-center space-x-3">
                        <Cpu className="w-5 h-5 text-blue-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">CPU</div>
                          <div className="text-lg font-semibold text-blue-600">
                            {stats.cpu_percent.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <MemoryStick className="w-5 h-5 text-green-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">Memory</div>
                          <div className="text-lg font-semibold text-green-600">
                            {stats.memory_percent.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Network className="w-5 h-5 text-purple-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">Network RX</div>
                          <div className="text-lg font-semibold text-purple-600">
                            {(stats.network_rx / 1024 / 1024).toFixed(1)} MB
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Activity className="w-5 h-5 text-orange-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">Network TX</div>
                          <div className="text-lg font-semibold text-orange-600">
                            {(stats.network_tx / 1024 / 1024).toFixed(1)} MB
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Log Controls */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Logs - {selectedContainer.name}
                    </h3>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700">Auto-refresh:</label>
                        <input
                          type="checkbox"
                          checked={autoRefresh}
                          onChange={(e) => setAutoRefresh(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                      
                      <input
                        type="text"
                        placeholder="Filter logs..."
                        value={logFilter}
                        onChange={(e) => setLogFilter(e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      
                      <select
                        value={maxLines}
                        onChange={(e) => setMaxLines(parseInt(e.target.value))}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={100}>100 lines</option>
                        <option value={500}>500 lines</option>
                        <option value={1000}>1000 lines</option>
                      </select>
                      
                      <button
                        onClick={() => isStreaming ? stopLogStream() : startLogStream(selectedContainer.id)}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium ${
                          isStreaming
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {isStreaming ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        <span>{isStreaming ? 'Stop' : 'Stream'}</span>
                      </button>
                      
                      <button
                        onClick={exportLogs}
                        disabled={logs.length === 0}
                        className="flex items-center space-x-1 px-3 py-1 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        <Download className="w-4 h-4" />
                        <span>Export</span>
                      </button>
                      
                      <button
                        onClick={() => restartContainer(selectedContainer.id)}
                        className="flex items-center space-x-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-md hover:bg-yellow-200"
                      >
                        <RefreshCw className="w-4 h-4" />
                        <span>Restart</span>
                      </button>
                    </div>
                  </div>

                  {/* Logs Display */}
                  <div className="bg-black text-green-400 p-4 rounded-md h-96 overflow-y-auto font-mono text-sm">
                    {filteredLogs.length === 0 ? (
                      <div className="text-gray-500">No logs available or loading...</div>
                    ) : (
                      filteredLogs.map((log, index) => (
                        <div key={index} className={`mb-1 ${log.type === 'error' ? 'text-red-400' : ''}`}>
                          <span className="text-gray-500 text-xs">
                            [{new Date(log.timestamp).toLocaleTimeString()}]
                          </span>
                          {' '}
                          <span>{log.message}</span>
                        </div>
                      ))
                    )}
                    <div ref={logsEndRef} />
                  </div>
                  
                  <div className="mt-2 text-xs text-gray-500 flex justify-between">
                    <span>Showing {filteredLogs.length} of {logs.length} log entries</span>
                    <span>{isStreaming ? '🟢 Live streaming' : '🔴 Static view'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12">
                <div className="text-center">
                  <Server className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No container selected</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Select a container from the list to view its logs and statistics.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DockerLogsViewer;
